// =============================================================================
// 레이아웃
// =============================================================================
export const MOBILE_MAX_WIDTH = 768;
export const MOBILE_FIXED_WIDTH = 450;
export const HEADER_HEIGHT = 64;
export const FOOTER_HEIGHT = 64;
export const PAGE_PADDING = 24;
export const BOTTOM_BTN_HEIGHT = 80;

// =============================================================================
// 케어 코치(챗봇)
// =============================================================================
/**
 * 케어 코치 '새 메시지' 읽음 상태 보관 키.
 * 서버는 무상태 — 읽은 메시지 key 목록(예: "stage,analysis")을 localStorage에 저장.
 * 메시지를 읽을 때마다 카운트가 줄고, 둘 다 읽으면 배지가 사라진다.
 * 로그인/로그아웃 시 초기화(AuthStore)하여 사용자 간 상태가 새지 않게 한다.
 */
export const CTBT_MSG_READ_KEY = 'ctbtMsgRead';

// =============================================================================
// 공통코드 - 부모코드명
// =============================================================================
export const COMMON_CODE_NAME = {
  ACNT_STTS_CD: 'ACNT_STTS_CD',
  GENDER_TYPE_CD: 'GENDER_TYPE_CD',
  USER_TYPE_CD: 'USER_TYPE_CD',
  TERMS_TYPE_CD: 'TERMS_TYPE_CD',
  HSPTL_CD: 'HSPTL_CD',
  MED_QST_TYPE_CD: 'MED_QST_TYPE_CD',
  MEDICINE_CD: 'MEDICINE_CD',
  ALARM_TYPE_CD: 'ALARM_TYPE_CD',
  FAQ_TYPE_CD: 'FAQ_TYPE_CD',
  OS_TYPE_CD: 'OS_TYPE_CD',
  FILTER_TYPE_CD: 'FILTER_TYPE_CD',
};

// =============================================================================
// 약관 유형 코드 (레거시 - 추후 정리 예정)
// =============================================================================
export const TERMS_TYPE_CODE = {
  SERVICE: 4000001,
  PRIVACY_POLICY: 4000002,
  REGISTER: 4000003,
  MARKETING: 4000004,
};

// =============================================================================
// 약관 사용 위치 코드 (TRMS_USE_PSTN_CD)
// =============================================================================
export const TERMS_USE_LOCATION = {
  NA: '820001', // N/A
  SIGNUP: '820002', // 회원가입
} as const;

// =============================================================================
// 사용자 유형 코드
// =============================================================================
export const USER_TYPE_CD = {
  USER: 1007001,
  ADMIN: 1007002,
  MEDICALSTAFF: 1007003,
};

// =============================================================================
// 챗봇 코드
// =============================================================================
export const CHATBOT_TYPE = {
  SPEAKER: {
    CHATBOT: 'chatbot',
    USER: 'user',
  },
  // 대화 유형
  CONV_TYPE: {
    MESSAGE: 'message',
    QUESTION: 'question',
    QUESTIONS: 'questions',
    END: 'end',
    RAG: 'RAG',
    CARD: 'card',
    PIECHART: 'piechart',
    IMAGE: 'image',
    USER: 'user',
    HISTORY: 'history_',
    HISTORY_MESSAGE: 'history_message',
    HISTORY_USER: 'history_user',
    HISTORY_IMAGE: 'history_image',
    HISTORY_PIECHART: 'history_piechart',
  },
  USER_OPTION: {
    BUTTON: 'button',
    CHECKBOX: 'checkbox',
    TEXT: 'text',
    SLIDER: 'slider',
    CHECKBOX_: 'checkbox_',
  },
};

// =============================================================================
// 알림 유형 코드 (GLP-1 도메인)
//
// 실제 BE 코드 값은 합의 후 확정.
// =============================================================================
export const ALARM_TYPE_CD = {
  MEDICINE: 5600001, // 약물 투여 알림
  WEIGHT_REMINDER: 5600002, // 체중 측정 알림
  MEAL_REMINDER: 5600003, // 식사 기록 알림
  WEEKLY_REPORT: 5600004, // 주간 리포트 알림
  COACHING: 5600005, // AI 코칭 알림
};

// =============================================================================
// 문진 유형 코드 (GLP-1 도메인용으로 갱신 — BE 합의 후 확정)
// =============================================================================
export const MED_QST_TYPE_CD = {
  BASICQUESTIONNAIRE: 5100001,
  DEMOGRAPHICINFORMATION: 5100002,
  LIFESTYLEINFORMATION: 5100003, // 생활습관 정보
};

// =============================================================================
// 약물 정보 (GLP-1 도메인)
//
// drugInfoSid는 BE DB 시퀀스와 매칭. 실제 값은 BE 확정 후 동기화.
// =============================================================================
export const DRUG_INFO = {
  WEGOVY: {
    medicineNm: '위고비',
    drugInfoSid: 1,
  },
  MOUNJARO: {
    medicineNm: '마운자로',
    drugInfoSid: 2,
  },
  SAXENDA: {
    medicineNm: '삭센다',
    drugInfoSid: 3,
  },
  NONE: {
    medicineNm: '없음',
    drugInfoSid: 99,
  },
};

// =============================================================================
// 공통 코드 PID (BE 합의 후 확정)
// =============================================================================
export const COMMON_CODE_PID = {
  // 예: SYMPTOM_TYPE_CD: 5900,
};

// =============================================================================
// 서비스 컨트롤 페이지 경로 (트래킹용)
// =============================================================================
export const SERVICE_CONTROL_PAGE_PATH = {
  login: 'login',
  findId: 'find-id',
  resetPwd: 'reset-pwd',
  register: 'register',
  verification: 'verification',
  home: 'home',
  calendar: 'calendar',
  myPage: 'my-page',
  editProfile: 'edit-profile',
  editMemberInfo: 'edit-member-info',
  termsSettings: 'terms-settings',
  notificationSettings: 'notification-settings',
  faq: 'faq',
  version: 'version',
  privacyPolicy: 'privacy-policy',
  service: 'service',
  appGuide: 'app-guide',
  medicalQuestion: 'medical-question',
  drugQuestion: 'drug-question',
  medicineSettings: 'medicine-settings',
  languageSettings: 'language-settings',
  personaSettings: 'persona-settings',
  // W2~ GLP-1 도메인 추가 예정:
  //   record:  'record',
  //   report:  'report',
  //   chat:    'chat',
};

// =============================================================================
// 언어
// =============================================================================
export const LANGUAGE = {
  korean: 'ko',
  english: 'en',
  deutsch: 'de',
};

// =============================================================================
// 차트 색상 팔레트 (chatbot piechart 등)
//
// GLP-1 sage green 톤
// =============================================================================
export const CHATBOT_CHART_COLOR: { code: string; color: string }[] = [
  { code: '5900001001', color: '#365A31' },
  { code: '5900001002', color: '#46723F' },
  { code: '5900001003', color: '#5C8E55' },
  { code: '5900001004', color: '#7AA672' },
  { code: '5900001005', color: '#9CBE96' },
  { code: '5900001006', color: '#C1D7BD' },
];
